import { suiGraphQlUrl } from '@/config/sui';

export interface OwnedCoinObject {
  objectId: string;
  version: string;
  digest: string;
}

export interface OwnedCoinObjectsResult {
  coins: OwnedCoinObject[];
  addressSeen: boolean;
  chainIdentifier?: string;
  addressBalance: string;
}

export const PAGE_SIZE = 50;

export const MAX_PAGES = 20;

const OWNED_COIN_OBJECTS_QUERY = `
  query OwnedCoinObjects(
    $owner: SuiAddress!
    $objectType: String!
    $coinType: String!
    $after: String
  ) {
    checkpoint(sequenceNumber: 0) { digest }
    address(address: $owner) {
      balance(coinType: $coinType) {
        addressBalance
      }
      objects(filter: { type: $objectType }, first: ${PAGE_SIZE}, after: $after) {
        pageInfo {
          hasNextPage
          endCursor
        }
        nodes {
          address
          digest
          version
        }
      }
    }
  }
`;

interface OwnedCoinObjectsGraphQlResponse {
  data?: {
    checkpoint?: { digest: string | null } | null;
    address: {
      balance?: { addressBalance: string | null } | null;
      objects: {
        pageInfo: { hasNextPage: boolean; endCursor: string | null };
        nodes: { address: string; digest: string | null; version: number | null }[];
      };
    } | null;
  };
  errors?: { message: string }[];
}

export async function getOwnedCoinObjects(
  owner: string,
  coinType: string,
): Promise<OwnedCoinObjectsResult> {
  const objectType = `0x2::coin::Coin<${coinType}>`;
  const coins: OwnedCoinObject[] = [];
  let after: string | null = null;
  let addressSeen = false;
  let chainIdentifier: string | undefined;
  let addressBalance = '0';

  for (let page = 0; page < MAX_PAGES; page += 1) {
    const response = await fetch(suiGraphQlUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query: OWNED_COIN_OBJECTS_QUERY,
        variables: { owner, objectType, coinType, after },
      }),
    });

    if (!response.ok) {
      throw new Error(`Sui GraphQL request failed with status ${response.status}`);
    }

    const body = (await response.json()) as OwnedCoinObjectsGraphQlResponse;
    if (body.errors?.length) {
      throw new Error(`Sui GraphQL request failed: ${body.errors[0]!.message}`);
    }

    if (!addressSeen) chainIdentifier = body.data?.checkpoint?.digest ?? undefined;
    const objects = body.data?.address?.objects;
    if (!objects) {
      if (addressSeen) {
        throw new Error(`Sui GraphQL returned an incomplete paginated result for ${coinType}`);
      }
      return {
        coins,
        addressSeen,
        addressBalance,
        ...(chainIdentifier ? { chainIdentifier } : {}),
      };
    }
    if (!addressSeen) {
      addressBalance = body.data?.address?.balance?.addressBalance ?? '0';
    }
    addressSeen = true;

    for (const node of objects.nodes) {
      coins.push({
        objectId: node.address,
        version: node.version != null ? String(node.version) : '',
        digest: node.digest ?? '',
      });
    }

    if (!objects.pageInfo.hasNextPage)
      return {
        coins,
        addressSeen,
        addressBalance,
        ...(chainIdentifier ? { chainIdentifier } : {}),
      };
    after = objects.pageInfo.endCursor;
  }

  throw new Error(
    `Sui GraphQL pagination for ${coinType} did not terminate within ${MAX_PAGES} pages`,
  );
}
